/**
 * Bookings on the phone website (web-account-pages D8; phone-signed-in/bookings.html): the app's
 * Bookings tab (BookingsView, its header and body) in the app's shell, fed the website session's
 * dashboard and past bookings. The chosen view is in the address (`?tab=past`), as in the app, so
 * Back from a booking returns to it. A booking opens its page; Back opens Home. Drawn inside the
 * phone tab layout (PhoneTabsLayout), its header in the frame's header slot.
 */
import { useLayoutEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useClientShellContext } from '@/components/client/ClientShell/ClientShellContext';
import { BookingsHeader, BookingsView, type BookingsTab } from '@/pages/client/BookingsView';

import { useWebPastBookings } from '../hooks/useWebAccount';

export function PhoneBookings() {
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const tab: BookingsTab = search.get('tab') === 'past' ? 'past' : 'upcoming';
  const chooseTab = (next: BookingsTab) => setSearch(next === 'past' ? { tab: 'past' } : {}, { replace: true });
  // Read once Past is opened, as the app does.
  const past = useWebPastBookings({ enabled: tab === 'past' });
  const pastItems = past.data?.pages.flatMap((p) => p.items) ?? [];
  const { dashboard, setHeaderSlot, scrollContentToTop } = useClientShellContext();

  // The header in the frame's slot, re-set only on a view change; the scroller returns to the top
  // with the view (Upcoming | Past), as the app's.
  useLayoutEffect(() => {
    setHeaderSlot(<BookingsHeader activeTab={tab} onTabChange={chooseTab} onBack={() => navigate('/')} />);
    scrollContentToTop();
    return () => setHeaderSlot(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-set on a view change only
  }, [setHeaderSlot, scrollContentToTop, tab]);

  return (
    <BookingsView
      tab={tab}
      upcoming={dashboard.upcoming}
      onOpen={(id) => navigate(`/account/bookings/${encodeURIComponent(id)}`)}
      past={{
        status: past.isPending ? 'loading' : past.isError ? 'error' : 'ready',
        items: pastItems,
        hasMore: !!past.hasNextPage,
        loadingMore: past.isFetchingNextPage,
        onMore: () => void past.fetchNextPage(),
      }}
    />
  );
}
